'use client'

import { useState } from 'react'
import { CheckCircle, X, Check } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { Text } from '@/components/ui/text'
import { useModal } from './ModalContext'

type QuizStep = {
  q: string
  sub: string
  options: string[]
}

export default function ModalQuiz() {
  const { activeModal, closeModal } = useModal()
  const isOpen = activeModal === 'quiz'
  const t = useTranslations('modals.quiz')

  const steps = t.raw('steps') as QuizStep[]

  const [step, setStep] = useState(0)
  const [selections, setSelections] = useState<(string | null)[]>(Array(steps.length).fill(null))
  const [submitted, setSubmitted] = useState(false)

  // Contact step
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const select = (val: string) => {
    const next = [...selections]
    next[step] = val
    setSelections(next)
  }

  const handleClose = () => {
    closeModal()
  }

  const handleSubmit = () => {
    // TODO: send quizData + contact info to API
    setSubmitted(true)
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('quizCompleted', '1')
    }
  }

  if (!isOpen) return null

  const inputClass =
    'w-full rounded-xl border border-border bg-white px-4 py-3 text-[15px] text-text-primary placeholder:text-text-subtle focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20'
  const labelClass = 'mb-2 block text-sm font-medium text-text-muted'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-text-primary/40 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative z-10 max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl border border-border bg-white p-8 shadow-[0_40px_80px_-20px_rgba(43,90,255,0.25)]">
        <div className="mb-6 flex items-center justify-between">
          <Heading as="h3" size="h3">{t('title')}</Heading>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t('closeLabel')}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-text-muted transition-colors hover:border-blue hover:text-blue"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Progress */}
        <div className="mb-6 flex items-center gap-2">
          {[...Array(steps.length + 1)].map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= step || submitted ? 'bg-grad-cta' : 'bg-border'
              }`}
            />
          ))}
        </div>

        {submitted ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-grad-cta text-white shadow-[0_10px_30px_-10px_rgba(43,90,255,0.5)]">
              <CheckCircle size={32} strokeWidth={1.75} />
            </div>
            <Heading as="h4" size="h4">{t('successTitle')}</Heading>
            <Text tone="muted">{t('successDesc')}</Text>
          </div>
        ) : step < steps.length ? (
          /* Option steps */
          <div>
            <Heading as="h4" size="h4" className="mb-1">{steps[step].q}</Heading>
            <Text tone="muted" size="sm" className="mb-5">{steps[step].sub}</Text>
            <div className="mb-6 flex flex-col gap-2">
              {steps[step].options.map((opt) => {
                const selected = selections[step] === opt
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => select(opt)}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-[15px] transition-all ${
                      selected
                        ? 'border-blue bg-blue/5 text-text-primary'
                        : 'border-border bg-white text-text-primary hover:border-blue/50 hover:bg-bg-off'
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                        selected ? 'border-blue bg-grad-cta text-white' : 'border-border text-transparent'
                      }`}
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>{opt}</span>
                  </button>
                )
              })}
            </div>
            <div className="flex items-center justify-between gap-3">
              {step > 0 ? (
                <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>
                  {t('backLabel')}
                </Button>
              ) : (
                <span />
              )}
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={!selections[step]}
              >
                {t('nextLabel')}
              </Button>
            </div>
          </div>
        ) : (
          /* Contact step */
          <div>
            <Heading as="h4" size="h4" className="mb-1">{t('contactTitle')}</Heading>
            <Text tone="muted" size="sm" className="mb-5">
              {t('contactSub')}
            </Text>
            <div className="mb-4">
              <label className={labelClass}>{t('fields.fullName')}</label>
              <input
                type="text"
                className={inputClass}
                placeholder={t('fields.fullNamePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="mb-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>{t('fields.email')}</label>
                <input
                  type="email"
                  className={inputClass}
                  placeholder={t('fields.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>{t('fields.phone')}</label>
                <input
                  type="tel"
                  className={inputClass}
                  placeholder={t('fields.phonePlaceholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>
                {t('backLabel')}
              </Button>
              <Button onClick={handleSubmit}>{t('submitLabel')}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
